<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideNewspaper } from 'lucide-vue-next'
import { categories } from '~/components/widget/faq-page/filter/mocks'
import { FeaturePagination } from '~/components/feature/pagination'
import { EntityFaqItems, EntityFaqSkeleton } from '~/components/entity/faq'
import type { SearchResponse } from 'meilisearch'
import type { IMeiliGlossaryHit } from '~/components/entity/glossary/types'
import type { IFaqQueryParams } from '~/components/widget/faq-list/types'

const config = useRuntimeConfig()

const props = withDefaults(
  defineProps<{
    searchType: string
    variant?: 'sidebar' | 'default'
    queryParams?: IFaqQueryParams
  }>(),
  {
    variant: 'default',
    queryParams: () => ({
      per_page: '20',
      q: '',
      filter: []
    })
  }
)

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const hitsPerPage = Number(props.queryParams?.per_page || 10)
const searchQueryString = ref(String(props.queryParams?.q || ''))
const debouncedQuery = useDebounce(searchQueryString, 500)
const currentPage = ref(Number(route.query.page) || 1)

const currentFilters = ref([...(props.queryParams?.filter || [])])

// Compute search key based on all filter dependencies
const searchKey = computed(() =>
  [
    `faq-${props.searchType}-search`,
    debouncedQuery.value,
    currentPage.value,
    currentFilters.value.join(',')
  ].join('-')
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IMeiliGlossaryHit> | null>(
  searchKey,
  async () => {
    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_articles`)

    try {
      const filters = currentFilters.value || []
      const filterQuery = [`(type = ${props.searchType})`]

      if (filters.length > 0 && filters.length < categories.length) {
        filterQuery.push(`(${filters.join(' OR ')})`)
      }

      return (await search(debouncedQuery.value, {
        hitsPerPage,
        sort: ['sort_order:asc'],
        page: currentPage.value,
        attributesToHighlight: ['title', 'description'],
        filter: filterQuery
      })) as unknown as SearchResponse<IMeiliGlossaryHit>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: [currentPage, debouncedQuery, currentFilters],
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    description: hit._formatted?.description[locale.value] || hit.description[locale.value]
  }))
})

const schemaFaq = computed(() => {
  if (!typedHits.value?.length) {
    return []
  }

  return typedHits.value.map((hit) => ({
    '@type': 'Question',
    name: hit.title,
    acceptedAnswer: {
      '@type': 'Answer',
      text: hit.description
    }
  }))
})

useSchemaOrg([
  computed(() => ({
    '@id': '#webpage',
    mainEntity: schemaFaq.value
  }))
])

watch(
  () => props.queryParams,
  (newParams) => {
    searchQueryString.value = newParams?.q || ''
    currentFilters.value = [...(newParams?.filter || [])]
    currentPage.value = 1
    router.replace({
      query: { ...route.query, page: undefined }
    })
  },
  { deep: true }
)

watch(debouncedQuery, () => {
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      q: debouncedQuery.value || undefined,
      page: undefined
    }
  })
})

const changePage = (page: number) => {
  currentPage.value = page
  router.replace({
    query: {
      ...route.query,
      q: searchQueryString.value || '',
      page
    }
  })
}

useHead(() => ({
  meta: !result.value?.hits?.length ? [{ name: 'robots', content: 'noindex, follow' }] : []
}))
</script>

<template>
  <div class="space-y-5">
    <section v-if="!isSearching && !hasError && result">
      <div v-if="result.hits?.length" class="Accordion">
        <EntityFaqItems :variant="props.variant" :items="typedHits" />

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityFaqSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('widget.faq_like.list.error_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
